import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { collection, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../config';

export interface FileInfo {
  id: string;
  name: string;
  url: string | null;
  size: number;
  type: string;
  uploadedAt: string;
  path: string;
  s3Key: string; // S3 object key
  s3Bucket: string; // S3 bucket name
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class AWSS3Service {
  private s3Client: S3Client | null = null;
  private bucketName: string | undefined;

  constructor() {
    this.initializeS3Client();
  }

  private initializeS3Client() {
    const region = process.env.REACT_APP_AWS_REGION;
    const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
    const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
    this.bucketName = process.env.REACT_APP_S3_BUCKET_NAME;

    // Debug: Log all environment variables
    console.log('🔍 AWS S3 Environment Variables Debug:');
    console.log('REACT_APP_AWS_REGION:', region);
    console.log('REACT_APP_AWS_ACCESS_KEY_ID:', accessKeyId ? '✅ Set' : '❌ Missing');
    console.log('REACT_APP_AWS_SECRET_ACCESS_KEY:', secretAccessKey ? '✅ Set' : '❌ Missing');
    console.log('REACT_APP_S3_BUCKET_NAME:', this.bucketName);
    console.log('All process.env keys:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

    if (region && accessKeyId && secretAccessKey && this.bucketName) {
      this.s3Client = new S3Client({
        region,
        credentials: {
          accessKeyId,
          secretAccessKey,
        },
      });
      console.log('✅ AWS S3 Service: Configured successfully.');
    } else {
      console.warn('⚠️ AWS S3 Service: Not configured. Missing environment variables.');
      console.warn('Missing variables:', {
        region: !region,
        accessKeyId: !accessKeyId,
        secretAccessKey: !secretAccessKey,
        bucketName: !this.bucketName
      });
      this.s3Client = null;
    }
  }
  
  // Upload file with specific S3 key
  async uploadFileWithKey(
    file: File, 
    s3Key: string, 
    onProgress?: (progress: UploadProgress) => void
  ): Promise<FileInfo> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      throw new Error('AWS S3 not configured');
    }

    try {
      console.log('📁 Uploading file to AWS S3 with specific key:', s3Key);
      
      // Convert File to Uint8Array for proper S3 upload
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      const uploadCommand = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: s3Key,
        Body: uint8Array,
        ContentType: file.type,
        Metadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString()
        }
      });

      await this.s3Client.send(uploadCommand);
      console.log('✅ File uploaded to S3 successfully with key:', s3Key);
      
      // Store metadata in Firestore (lightweight)
      const docRef = await addDoc(collection(db, 'fileStorage'), {
        id: s3Key.split('/').pop() || 'unknown',
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        path: s3Key,
        s3Key: s3Key,
        s3Bucket: this.bucketName,
      });
      
      console.log('✅ File metadata stored in Firestore:', docRef.id);
      
      return {
        id: s3Key.split('/').pop() || 'unknown',
        name: file.name,
        url: null,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        path: s3Key,
        s3Key: s3Key,
        s3Bucket: this.bucketName
      };
      
    } catch (error) {
      console.error('❌ S3 Upload error:', error);
      throw new Error(`S3 upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  async uploadFile(
    file: File, 
    pathPrefix: string, 
    onProgress?: (progress: UploadProgress) => void
  ): Promise<FileInfo> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      throw new Error('AWS S3 not configured');
    }

    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const safeName = file.name.replace(/[^\w\.\-]+/g, '_');
    const s3Key = `${pathPrefix}/${fileId}_${safeName}`;
    
    try {
      console.log('📁 Uploading file to AWS S3:', s3Key);
      
      // Simulate progress for UI feedback
      if (onProgress) {
        onProgress({
          loaded: 0,
          total: file.size,
          percentage: 0
        });
      }
      
      // Convert File to ArrayBuffer for browser compatibility
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Upload to S3
      const uploadCommand = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: s3Key,
        Body: uint8Array,
        ContentType: file.type,
        Metadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
          fileId: fileId
        },
        // Add CORS headers to help with browser compatibility
        CacheControl: 'no-cache',
        ACL: 'private' // Ensure private access
      });
      
      await this.s3Client.send(uploadCommand);
      console.log('✅ File uploaded to S3 successfully');
      
      // Don't store presigned URLs as they expire
      // Instead, store only the S3 key and bucket info
      // URLs will be generated dynamically when needed
      console.log('🔗 File uploaded successfully, storing metadata');
      
      // Store metadata in Firestore (lightweight)
      const docRef = await addDoc(collection(db, 'fileStorage'), {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        path: s3Key,
        s3Key: s3Key,
        s3Bucket: this.bucketName,
        // Don't store presigned URL as it expires
        // URL will be generated dynamically when needed
      });
      
      // Simulate progress completion
      if (onProgress) {
        onProgress({
          loaded: file.size,
          total: file.size,
          percentage: 100
        });
      }
      
      console.log('✅ File metadata stored in Firestore:', docRef.id);
      
      return {
        id: fileId,
        name: file.name,
        url: null, // Don't return presigned URL as it expires
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        path: s3Key,
        s3Key: s3Key,
        s3Bucket: this.bucketName
      };
      
    } catch (error) {
      console.error('❌ S3 Upload error:', error);
      throw new Error(`S3 upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  async getFileUrl(fileInfo: FileInfo): Promise<string | null> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      console.warn('AWS S3 not configured, cannot get file URL.');
      return null;
    }
    
    try {
      console.log('🔍 Getting file URL from S3 for:', fileInfo.id);
      
      // Always generate a fresh presigned URL to avoid expiration issues
      // Don't rely on cached URLs as they expire
      const getCommand = new GetObjectCommand({
        Bucket: fileInfo.s3Bucket || this.bucketName,
        Key: fileInfo.s3Key || fileInfo.path
      });
      
      // Generate a presigned URL that is valid for 7 days (604800 seconds)
      // This gives users plenty of time to access files
      const presignedUrl = await getSignedUrl(this.s3Client, getCommand, { expiresIn: 604800 });
      console.log('✅ Generated fresh presigned URL (7 days validity)');
      
      return presignedUrl;
      
    } catch (error) {
      console.error('❌ Error getting S3 file URL:', error);
      return null;
    }
  }
  
  async deleteFile(fileInfo: FileInfo): Promise<void> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      throw new Error('AWS S3 not configured');
    }
    
    try {
      console.log('🗑️ Deleting file from S3:', fileInfo.id);
      
      // Delete from S3
      const deleteCommand = new DeleteObjectCommand({
        Bucket: fileInfo.s3Bucket || this.bucketName,
        Key: fileInfo.s3Key || fileInfo.path
      });
      
      await this.s3Client.send(deleteCommand);
      console.log('✅ File deleted from S3');
      
      // Delete metadata from Firestore
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      const fileQuery = query(
        collection(db, 'fileStorage'), 
        where('id', '==', fileInfo.id)
      );
      
      const fileSnapshot = await getDocs(fileQuery);
      
      if (!fileSnapshot.empty) {
        const fileDoc = fileSnapshot.docs[0];
        await deleteDoc(doc(db, 'fileStorage', fileDoc.id));
        console.log('✅ File metadata deleted from Firestore');
      }
      
    } catch (error) {
      console.error('❌ S3 Delete error:', error);
      throw new Error(`S3 delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  // Generate a presigned URL for direct upload (for large files)
  async generatePresignedUploadUrl(
    fileName: string, 
    fileType: string, 
    pathPrefix: string
  ): Promise<{ uploadUrl: string; fileId: string; s3Key: string }> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      throw new Error('AWS S3 not configured');
    }
    
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const safeName = fileName.replace(/[^\w\.\-]+/g, '_');
    const s3Key = `${pathPrefix}/${fileId}_${safeName}`;
    
    const putCommand = new PutObjectCommand({
      Bucket: this.bucketName,
      Key: s3Key,
      ContentType: fileType,
      Metadata: {
        originalName: fileName,
        uploadedAt: new Date().toISOString(),
        fileId: fileId
      }
    });
    
    const presignedUrl = await getSignedUrl(this.s3Client, putCommand, { expiresIn: 3600 });
    
    return {
      uploadUrl: presignedUrl,
      fileId: fileId,
      s3Key: s3Key
    };
  }
  
  // Upload string content to S3
  async uploadStringContent(
    bucketName: string,
    key: string,
    content: string,
    contentType: string = 'application/json'
  ): Promise<void> {
    if (!this.isConfigured() || !this.s3Client) {
      throw new Error('AWS S3 not configured');
    }

    try {
      console.log(`📁 Uploading string content to S3: ${key}`);
      
      const uploadCommand = new PutObjectCommand({
        Bucket: bucketName,
        Key: key,
        Body: content,
        ContentType: contentType,
        Metadata: {
          uploadedAt: new Date().toISOString(),
          type: 'string-content'
        },
        CacheControl: 'no-cache',
        ACL: 'private'
      });
      
      await this.s3Client.send(uploadCommand);
      console.log('✅ String content uploaded to S3 successfully');
    } catch (error) {
      console.error('❌ S3 Upload string error:', error);
      throw new Error(`S3 upload string failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }


  // List files in S3 bucket with prefix
  async listFiles(bucketName: string, prefix: string): Promise<{ Key: string; LastModified?: Date; Size?: number }[]> {
    if (!this.isConfigured() || !this.s3Client) {
      throw new Error('AWS S3 not configured');
    }

    try {
      const command = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: prefix,
        MaxKeys: 1000
      });

      const response = await this.s3Client.send(command);
      return response.Contents?.map(obj => ({
        Key: obj.Key!,
        LastModified: obj.LastModified,
        Size: obj.Size
      })) || [];
    } catch (error) {
      console.error('❌ S3 List files error:', error);
      throw new Error(`S3 list files failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Download file from S3
  async downloadFile(bucketName: string, key: string): Promise<string> {
    if (!this.isConfigured() || !this.s3Client) {
      throw new Error('AWS S3 not configured');
    }

    try {
      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: key
      });

      const response = await this.s3Client.send(command);
      const body = await response.Body?.transformToString();
      
      if (!body) {
        throw new Error('Empty response body');
      }

      return body;
    } catch (error) {
      console.error('❌ S3 Download error:', error);
      throw new Error(`S3 download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Check if AWS S3 is configured
  isConfigured(): boolean {
    return this.s3Client !== null && this.bucketName !== undefined;
  }

  // Bulk operations
  async uploadMultipleFiles(
    files: FileList, 
    pathPrefix: string,
    onProgress?: (fileIndex: number, progress: UploadProgress) => void
  ): Promise<FileInfo[]> {
    const results: FileInfo[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fileInfo = await this.uploadFile(
          file,
          pathPrefix,
          (progress) => {
            onProgress?.(i, progress);
          }
        );
        results.push(fileInfo);
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        throw error;
      }
    }
    
    return results;
  }

  // Upload JSON data directly to S3
  async uploadJsonData(s3Key: string, jsonData: string): Promise<void> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      throw new Error('AWS S3 not configured');
    }

    try {
      console.log('📁 Uploading JSON data to AWS S3:', s3Key);
      
      const uploadCommand = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: s3Key,
        Body: jsonData,
        ContentType: 'application/json',
        Metadata: {
          uploadedAt: new Date().toISOString()
        }
      });

      await this.s3Client.send(uploadCommand);
      console.log('✅ JSON data uploaded to S3 successfully');
    } catch (error) {
      console.error('❌ S3 JSON Upload error:', error);
      throw new Error(`S3 JSON upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Download JSON data from S3
  async downloadJsonData(s3Key: string): Promise<string | null> {
    if (!this.isConfigured() || !this.s3Client || !this.bucketName) {
      console.warn('AWS S3 not configured, cannot download JSON data.');
      return null;
    }

    try {
      console.log('📥 Downloading JSON data from S3:', s3Key);
      
      const command = new GetObjectCommand({
        Bucket: this.bucketName,
        Key: s3Key
      });

      const response = await this.s3Client.send(command);
      const body = await response.Body?.transformToString();
      
      if (!body) {
        console.log('⚠️ No data found for key:', s3Key);
        return null;
      }

      console.log('✅ JSON data downloaded successfully');
      return body;
    } catch (error: any) {
      // If file doesn't exist, return null instead of throwing error
      if (error.name === 'NoSuchKey' || error.$metadata?.httpStatusCode === 404) {
        console.log('ℹ️ File not found in S3:', s3Key);
        return null;
      }
      console.error('❌ S3 JSON Download error:', error);
      throw new Error(`S3 JSON download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export default new AWSS3Service();
